import type {
  ModelsResponse,
  ModelDataResponse,
  ModelDataOptions,
  DatabaseApiError,
} from '@/types/database'

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

function getAuthHeaders(): HeadersInit {
  return {
    'Accept': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  }
}

async function handleApiResponse<T>(response: Response, fallbackError: string): Promise<T> {
  if (!response.ok) {
    let errorMessage = fallbackError
    try {
      const data = await response.json() as DatabaseApiError
      errorMessage = data.error || errorMessage
    } catch {
      // Response wasn't JSON
    }
    throw new Error(errorMessage)
  }
  return response.json() as Promise<T>
}

export async function fetchProductionModels(projectId: number): Promise<ModelsResponse> {
  const response = await fetch(`/api/projects/${projectId}/production_database/models`, {
    method: 'GET',
    headers: getAuthHeaders(),
  })
  return handleApiResponse<ModelsResponse>(response, 'Failed to fetch production models')
}

export async function fetchProductionModelData(
  projectId: number,
  appLabel: string,
  modelName: string,
  options: ModelDataOptions = {}
): Promise<ModelDataResponse> {
  const params = new URLSearchParams()
  if (options.page) params.set('page', String(options.page))
  if (options.page_size) params.set('page_size', String(options.page_size))
  if (options.order_by) params.set('order_by', options.order_by)
  if (options.search) params.set('search', options.search)

  const queryString = params.toString()
  const encodedAppLabel = encodeURIComponent(appLabel)
  const encodedModelName = encodeURIComponent(modelName)
  const url = `/api/projects/${projectId}/production_database/models/${encodedAppLabel}/${encodedModelName}/data${queryString ? `?${queryString}` : ''}`

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  })
  return handleApiResponse<ModelDataResponse>(response, 'Failed to fetch production model data')
}

export async function refreshProductionModels(projectId: number): Promise<ModelsResponse> {
  const response = await fetch(`/api/projects/${projectId}/production_database/refresh`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
  })
  return handleApiResponse<ModelsResponse>(response, 'Failed to refresh production models')
}
